import { createSlice } from "@reduxjs/toolkit";
import {
  // CRUD thunks
  createOfferThunk,
  getOffersThunk,
  getOfferByIdThunk,
  updateOfferThunk,
  deleteOfferThunk,
  updateOfferStatusThunk,

  // Validation thunks
  validateOfferCodeThunk,
  testOfferThunk,

  // Analytics thunks
  getOfferAnalyticsThunk,
  getOfferUsageThunk,
  getCustomerUsageThunk,
  getBranchUsageThunk,

  // Approval workflow thunks
  approveOfferThunk,
  rejectOfferThunk,
  getPendingApprovalsThunk,

  // Bulk operations thunks
  bulkCreateOffersThunk,
  bulkUpdateOffersThunk,
  bulkUpdateStatusThunk,
  bulkDeleteOffersThunk,

  // Template thunks
  getOfferTemplatesThunk,
  createFromTemplateThunk,

  // Import/Export thunks
  importOffersThunk,
  exportOffersThunk,

  // Scheduling thunks
  scheduleOfferThunk,
  getScheduledOffersThunk,

  // Branch-specific thunks
  getOffersByBranchThunk,
  cloneOfferThunk,

  // Reports thunks
  getPerformanceReportThunk,
  getExpiringOffersThunk,
  getPopularOffersThunk,

  // Actions
  clearOfferError,
  clearOfferValidation,
  clearOfferAnalytics,
  resetOfferState,
  setCurrentOffer,
  clearCurrentOffer,
} from "../thunks/offerThunks";

const initialState = {
  // Offers list
  offers: [],
  branchOffers: [],
  scheduledOffers: [],
  pendingApprovals: [],

  // Single offer
  currentOffer: null,

  // Validation & Testing
  offerCodeValidation: null,
  offerTestResults: null,

  // Analytics
  offerAnalytics: null,
  offerUsage: null,
  customerUsage: null,
  branchUsage: null,

  // Templates
  offerTemplates: [],

  // Reports
  performanceReport: null,
  expiringOffers: [],
  popularOffers: [],

  // Import/Export results
  importResults: null,
  exportStatus: null,

  // Loading states
  loading: false,
  saving: false,
  validating: false,
  bulkProcessing: false,
  analyticsLoading: false,
  reportsLoading: false,
  templatesLoading: false,

  // Errors
  error: null,
  validationError: null,
  bulkError: null,

  // UI State
  selectedOfferIds: [],
  filters: {
    type: null,
    status: null,
    branchCode: null,
    isActive: null,
    approvalStatus: null,
    dateRange: null,
    search: null,
  },
  sortBy: "createdAt",
  sortOrder: "desc",

  // Pagination
  page: 1,
  limit: 20,
  totalCount: 0,
  totalPages: 0, // ✅ ADDED
};

const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    // Selection management
    setSelectedOfferIds: (state, action) => {
      state.selectedOfferIds = action.payload;
    },
    toggleOfferSelection: (state, action) => {
      const id = action.payload;
      const index = state.selectedOfferIds.indexOf(id);
      if (index > -1) {
        state.selectedOfferIds.splice(index, 1);
      } else {
        state.selectedOfferIds.push(id);
      }
    },
    selectAllOffers: (state) => {
      state.selectedOfferIds = state.offers.map((offer) => offer._id);
    },
    deselectAllOffers: (state) => {
      state.selectedOfferIds = [];
    },

    // Filter management
    setOfferFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearOfferFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Sorting
    setSortBy: (state, action) => {
      if (typeof action.payload === "string") {
        state.sortBy = action.payload;
      } else {
        state.sortBy = action.payload.sortBy;
        state.sortOrder = action.payload.sortOrder || "desc";
      }
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GENERAL ACTIONS =====
      .addCase(clearOfferError, (state) => {
        state.error = null;
        state.validationError = null;
        state.bulkError = null;
      })
      .addCase(clearOfferValidation, (state) => {
        state.offerCodeValidation = null;
        state.validationError = null;
      })
      .addCase(clearOfferAnalytics, (state) => {
        state.offerAnalytics = null;
        state.offerUsage = null;
        state.customerUsage = null;
        state.branchUsage = null;
      })
      .addCase(resetOfferState, () => initialState)
      .addCase(setCurrentOffer, (state, action) => {
        state.currentOffer = action.payload;
      })
      .addCase(clearCurrentOffer, (state) => {
        state.currentOffer = null;
      })

      // ===== CRUD OPERATIONS =====

      // Create Offer
      .addCase(createOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const newOffer = action.payload.data || action.payload;
        state.offers.unshift(newOffer);
        state.totalCount += 1;
      })
      .addCase(createOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // Get Offers
      .addCase(getOffersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOffersThunk.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload;
        state.offers = response.data || response.offers || response;
        state.totalCount = response.pagination?.total || state.offers.length;
        state.totalPages =
          response.pagination?.totalPages ||
          Math.ceil(state.totalCount / state.limit); // ✅ ADDED
        state.page = response.pagination?.page || state.page; // ✅ ADDED
      })
      .addCase(getOffersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Offer by ID
      .addCase(getOfferByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOfferByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOffer = action.payload.data || action.payload;
      })
      .addCase(getOfferByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Offer
      .addCase(updateOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const updatedOffer = action.payload.data || action.payload;
        const index = state.offers.findIndex(
          (offer) => offer._id === updatedOffer._id
        );
        if (index !== -1) {
          state.offers[index] = updatedOffer;
        }
        if (state.currentOffer?._id === updatedOffer._id) {
          state.currentOffer = updatedOffer;
        }
      })
      .addCase(updateOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // Delete Offer
      .addCase(deleteOfferThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOfferThunk.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId =
          action.payload.deletedId ||
          action.payload.deletedOffer?.id ||
          action.meta.arg;
        state.offers = state.offers.filter((offer) => offer._id !== deletedId);
        state.totalCount = Math.max(0, state.totalCount - 1);
        if (state.currentOffer?._id === deletedId) {
          state.currentOffer = null;
        }
      })
      .addCase(deleteOfferThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Offer Status
      .addCase(updateOfferStatusThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateOfferStatusThunk.fulfilled, (state, action) => {
        state.saving = false;
        const updatedOffer = action.payload.data || action.payload;
        const index = state.offers.findIndex(
          (offer) => offer._id === updatedOffer._id
        );
        if (index !== -1) {
          state.offers[index] = updatedOffer;
        }
        if (state.currentOffer?._id === updatedOffer._id) {
          state.currentOffer = updatedOffer;
        }
      })
      .addCase(updateOfferStatusThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // ===== VALIDATION =====

      // Validate Offer Code
      .addCase(validateOfferCodeThunk.pending, (state) => {
        state.validating = true;
        state.validationError = null;
      })
      .addCase(validateOfferCodeThunk.fulfilled, (state, action) => {
        state.validating = false;
        state.offerCodeValidation = action.payload.data || action.payload;
      })
      .addCase(validateOfferCodeThunk.rejected, (state, action) => {
        state.validating = false;
        state.validationError = action.payload;
      })

      // Test Offer
      .addCase(testOfferThunk.pending, (state) => {
        state.validating = true;
        state.error = null;
      })
      .addCase(testOfferThunk.fulfilled, (state, action) => {
        state.validating = false;
        state.offerTestResults = action.payload.data || action.payload;
      })
      .addCase(testOfferThunk.rejected, (state, action) => {
        state.validating = false;
        state.error = action.payload;
      })

      // ===== ANALYTICS =====

      // Get Offer Analytics
      .addCase(getOfferAnalyticsThunk.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(getOfferAnalyticsThunk.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.offerAnalytics = action.payload.data || action.payload;
      })
      .addCase(getOfferAnalyticsThunk.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      // Get Offer Usage
      .addCase(getOfferUsageThunk.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(getOfferUsageThunk.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.offerUsage = action.payload.data || action.payload;
      })
      .addCase(getOfferUsageThunk.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      // Get Customer Usage
      .addCase(getCustomerUsageThunk.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(getCustomerUsageThunk.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.customerUsage = action.payload.data || action.payload;
      })
      .addCase(getCustomerUsageThunk.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      // Get Branch Usage
      .addCase(getBranchUsageThunk.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(getBranchUsageThunk.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.branchUsage = action.payload.data || action.payload;
      })
      .addCase(getBranchUsageThunk.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      // ===== APPROVAL WORKFLOW =====

      // Approve Offer
      .addCase(approveOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(approveOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const approvedOffer = action.payload.data || action.payload;

        // Update in offers list
        const index = state.offers.findIndex(
          (o) => o._id === approvedOffer._id
        );
        if (index !== -1) {
          state.offers[index] = approvedOffer;
        }

        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (o) => o._id !== approvedOffer._id
        );

        // Update current offer
        if (state.currentOffer?._id === approvedOffer._id) {
          state.currentOffer = approvedOffer;
        }
      })
      .addCase(approveOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // Reject Offer
      .addCase(rejectOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(rejectOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const rejectedOffer = action.payload.data || action.payload;

        // Update in offers list
        const index = state.offers.findIndex(
          (o) => o._id === rejectedOffer._id
        );
        if (index !== -1) {
          state.offers[index] = rejectedOffer;
        }

        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (o) => o._id !== rejectedOffer._id
        );

        // Update current offer
        if (state.currentOffer?._id === rejectedOffer._id) {
          state.currentOffer = rejectedOffer;
        }
      })
      .addCase(rejectOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // Get Pending Approvals
      .addCase(getPendingApprovalsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingApprovalsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload;
        state.pendingApprovals = response.data || response;
      })
      .addCase(getPendingApprovalsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== BULK OPERATIONS =====

      // Bulk Create Offers
      .addCase(bulkCreateOffersThunk.pending, (state) => {
        state.bulkProcessing = true;
        state.bulkError = null;
      })
      .addCase(bulkCreateOffersThunk.fulfilled, (state, action) => {
        state.bulkProcessing = false;
        const response = action.payload;
        // Optionally add successful creates to offers list
        if (response.results) {
          const newOffers = response.results
            .filter((r) => r.success)
            .map((r) => r.offer);
          state.offers = [...newOffers, ...state.offers];
          state.totalCount += newOffers.length;
        }
      })
      .addCase(bulkCreateOffersThunk.rejected, (state, action) => {
        state.bulkProcessing = false;
        state.bulkError = action.payload;
      })

      // Bulk Update Offers
      .addCase(bulkUpdateOffersThunk.pending, (state) => {
        state.bulkProcessing = true;
        state.bulkError = null;
      })
      .addCase(bulkUpdateOffersThunk.fulfilled, (state, action) => {
        state.bulkProcessing = false;
        // Optionally refresh offers list after bulk update
      })
      .addCase(bulkUpdateOffersThunk.rejected, (state, action) => {
        state.bulkProcessing = false;
        state.bulkError = action.payload;
      })

      // Bulk Update Status
      .addCase(bulkUpdateStatusThunk.pending, (state) => {
        state.bulkProcessing = true;
        state.bulkError = null;
      })
      .addCase(bulkUpdateStatusThunk.fulfilled, (state, action) => {
        state.bulkProcessing = false;
        const { offerIds, isActive } = action.payload;

        // Update offers in list
        state.offers = state.offers.map((offer) =>
          offerIds.includes(offer._id) ? { ...offer, isActive } : offer
        );

        // Clear selection
        state.selectedOfferIds = [];
      })
      .addCase(bulkUpdateStatusThunk.rejected, (state, action) => {
        state.bulkProcessing = false;
        state.bulkError = action.payload;
      })

      // Bulk Delete Offers
      .addCase(bulkDeleteOffersThunk.pending, (state) => {
        state.bulkProcessing = true;
        state.bulkError = null;
      })
      .addCase(bulkDeleteOffersThunk.fulfilled, (state, action) => {
        state.bulkProcessing = false;
        const deletedIds = action.payload.deletedIds || action.meta.arg;

        // Remove deleted offers
        state.offers = state.offers.filter(
          (offer) => !deletedIds.includes(offer._id)
        );
        state.totalCount = Math.max(0, state.totalCount - deletedIds.length);

        // Clear selection
        state.selectedOfferIds = [];
      })
      .addCase(bulkDeleteOffersThunk.rejected, (state, action) => {
        state.bulkProcessing = false;
        state.bulkError = action.payload;
      })

      // ===== TEMPLATES =====

      // Get Offer Templates
      .addCase(getOfferTemplatesThunk.pending, (state) => {
        state.templatesLoading = true;
        state.error = null;
      })
      .addCase(getOfferTemplatesThunk.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.offerTemplates = action.payload.data || action.payload;
      })
      .addCase(getOfferTemplatesThunk.rejected, (state, action) => {
        state.templatesLoading = false;
        state.error = action.payload;
      })

      // Create from Template
      .addCase(createFromTemplateThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createFromTemplateThunk.fulfilled, (state, action) => {
        state.saving = false;
        const newOffer = action.payload.data || action.payload;
        state.offers.unshift(newOffer);
        state.totalCount += 1;
      })
      .addCase(createFromTemplateThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // ===== IMPORT/EXPORT =====

      // Import Offers
      .addCase(importOffersThunk.pending, (state) => {
        state.bulkProcessing = true;
        state.bulkError = null;
      })
      .addCase(importOffersThunk.fulfilled, (state, action) => {
        state.bulkProcessing = false;
        state.importResults = action.payload;
      })
      .addCase(importOffersThunk.rejected, (state, action) => {
        state.bulkProcessing = false;
        state.bulkError = action.payload;
      })

      // Export Offers
      .addCase(exportOffersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportOffersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.exportStatus = action.payload;
      })
      .addCase(exportOffersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== SCHEDULING =====

      // Schedule Offer
      .addCase(scheduleOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(scheduleOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const scheduledOffer = action.payload.data || action.payload;

        // Update in offers list
        const index = state.offers.findIndex(
          (o) => o._id === scheduledOffer.data?.offerId
        );
        if (index !== -1 && scheduledOffer.data?.offer) {
          state.offers[index] = scheduledOffer.data.offer;
        }
      })
      .addCase(scheduleOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // Get Scheduled Offers
      .addCase(getScheduledOffersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getScheduledOffersThunk.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload.data || action.payload;
        state.scheduledOffers = response.scheduled || [];
      })
      .addCase(getScheduledOffersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== BRANCH-SPECIFIC =====

      // Get Offers by Branch
      .addCase(getOffersByBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOffersByBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload;
        state.branchOffers = response.data || response;
      })
      .addCase(getOffersByBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Clone Offer
      .addCase(cloneOfferThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(cloneOfferThunk.fulfilled, (state, action) => {
        state.saving = false;
        const clonedOffer = action.payload.data || action.payload;
        state.offers.unshift(clonedOffer);
        state.totalCount += 1;
      })
      .addCase(cloneOfferThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // ===== REPORTS =====

      // Get Performance Report
      .addCase(getPerformanceReportThunk.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(getPerformanceReportThunk.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.performanceReport = action.payload.data || action.payload;
      })
      .addCase(getPerformanceReportThunk.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload;
      })

      // Get Expiring Offers
      .addCase(getExpiringOffersThunk.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(getExpiringOffersThunk.fulfilled, (state, action) => {
        state.reportsLoading = false;
        const response = action.payload;
        state.expiringOffers = response.data || response;
      })
      .addCase(getExpiringOffersThunk.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload;
      })

      // Get Popular Offers
      .addCase(getPopularOffersThunk.pending, (state) => {
        state.reportsLoading = true;
        state.error = null;
      })
      .addCase(getPopularOffersThunk.fulfilled, (state, action) => {
        state.reportsLoading = false;
        const response = action.payload;
        state.popularOffers = response.data || response;
      })
      .addCase(getPopularOffersThunk.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedOfferIds,
  toggleOfferSelection,
  selectAllOffers,
  deselectAllOffers,
  setOfferFilters,
  clearOfferFilters,
  setSortBy,
  setPage,
  setLimit,
} = offerSlice.actions;

// Selectors
export const selectOffers = (state) => state.offers.offers;
export const selectCurrentOffer = (state) => state.offers.currentOffer;
export const selectOffersLoading = (state) => state.offers.loading;
export const selectOffersSaving = (state) => state.offers.saving;
export const selectOffersError = (state) => state.offers.error;
export const selectOfferCodeValidation = (state) =>
  state.offers.offerCodeValidation;
export const selectOfferTestResults = (state) => state.offers.offerTestResults;
export const selectOfferAnalytics = (state) => state.offers.offerAnalytics;
export const selectPendingApprovals = (state) => state.offers.pendingApprovals;
export const selectScheduledOffers = (state) => state.offers.scheduledOffers;
export const selectOfferTemplates = (state) => state.offers.offerTemplates;
export const selectSelectedOfferIds = (state) => state.offers.selectedOfferIds;
export const selectOfferFilters = (state) => state.offers.filters;
export const selectPerformanceReport = (state) =>
  state.offers.performanceReport;
export const selectExpiringOffers = (state) => state.offers.expiringOffers;
export const selectPopularOffers = (state) => state.offers.popularOffers;

// ✅ ADDED: Pagination selector
export const selectOfferPagination = (state) => ({
  page: state.offers.page,
  limit: state.offers.limit,
  totalCount: state.offers.totalCount,
  totalPages: state.offers.totalPages,
});

export default offerSlice.reducer;
